import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../../styles/main.scss';
import api from '../../services/api';
import { networkManager } from '../../config/NetworkManager';
import { IoSend } from "react-icons/io5";
import { IoClose } from "react-icons/io5";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import ConversionCheckpoints from './ConversionCheckpoints';
import TestCases from './TestCases';
import Artifacts from './Artifacts';
import FormFeedback from './FormFeedback';
import ConversionTaskMonitor from '../common/ConversionTaskMonitor';

const ChatInterface = () => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportContent, setReportContent] = useState('');
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [isLoadingReportContent, setIsLoadingReportContent] = useState(false);
    const [showArtifactsModal, setShowArtifactsModal] = useState(false);
    const [showFormFeedbackModal, setShowFormFeedbackModal] = useState(false);
    const [showCheckpointsModal, setShowCheckpointsModal] = useState(false);
    const [showTestCasesModal, setShowTestCasesModal] = useState(false);
    const [reportType, setReportType] = useState('metadata'); // 'metadata', 'html', 'ts', 'integrated', 'backend'
    const [reportTypes, setReportTypes] = useState([]);
    const [activeFormName, setActiveFormName] = useState(null); // Form name for active conversion
    const [activeCheckpoints, setActiveCheckpoints] = useState(null); // Checkpoints for active form
    const [isLoadingCheckpoints, setIsLoadingCheckpoints] = useState(false);
    const [lastCheckedTimestamp, setLastCheckedTimestamp] = useState(null); // Track when we last checked for new checkpoints
    // messagesEndRef removed - now using direct container scrolling
    const inputRef = useRef(null);
    const loadingIntervalRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const checkpointPollIntervalRef = useRef(null);
    const recentCheckpointsPollIntervalRef = useRef(null);
    const handledCompletedTaskIdsRef = useRef(new Set());
    const handledFailedTaskIdsRef = useRef(new Set());

    // Progressive loading messages
    const loadingMessages = [
        "Agent is analysing your request...",
        "Agent is converting your request...",
        "Agent is generating the code...",
        "Agent is finalizing the output...",
        "Agent is preparing the response..."
    ];

    // Auto-scroll to bottom when new messages are added - only within chat container
    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            // Smooth scroll the messages container to the bottom, not the entire page
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loadingMessage]);

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Handle scrollbar visibility during scroll
    useEffect(() => {
        const messagesContainer = messagesContainerRef.current;
        if (!messagesContainer) return;

        const handleScroll = () => {
            // Add scrolling class to show scrollbar
            messagesContainer.classList.add('scrolling');
            
            // Clear previous timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            
            // Hide scrollbar after scroll stops
            scrollTimeoutRef.current = setTimeout(() => {
                messagesContainer.classList.remove('scrolling');
            }, 1000); // Hide 1 second after scrolling stops
        };

        messagesContainer.addEventListener('scroll', handleScroll);
        
        return () => {
            messagesContainer.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Typing animation effect
    useEffect(() => {
        if (isTyping && isLoading && loadingMessageIndex < loadingMessages.length) {
            const message = loadingMessages[loadingMessageIndex];
            let currentIndex = 0;
            
            const typeChar = () => {
                if (currentIndex < message.length) {
                    setLoadingMessage(message.substring(0, currentIndex + 1));
                    currentIndex++;
                    typingTimeoutRef.current = setTimeout(typeChar, 30); // Typing speed
                } else {
                    setIsTyping(false);
                }
            };
            
            setLoadingMessage('');
            typeChar();
            
            return () => {
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }
            };
        }
    }, [isTyping, loadingMessageIndex, isLoading]);

    // Progressive loading messages effect
    useEffect(() => {
        if (isLoading) {
            setLoadingMessageIndex(0);
            setIsTyping(true);
            
            // Change message every 45-60 seconds (adjust based on 5-minute average)
            loadingIntervalRef.current = setInterval(() => {
                setLoadingMessageIndex(prev => {
                    const nextIndex = prev + 1;
                    if (nextIndex < loadingMessages.length) {
                        setIsTyping(true);
                        return nextIndex;
                    }
                    return prev;
                });
            }, 50000); // Change message every 50 seconds
            
            return () => {
                if (loadingIntervalRef.current) {
                    clearInterval(loadingIntervalRef.current);
                }
            };
        } else {
            if (loadingIntervalRef.current) {
                clearInterval(loadingIntervalRef.current);
            }
            setLoadingMessage('');
            setLoadingMessageIndex(0);
            setIsTyping(false);
        }
    }, [isLoading]);

    // Update loading message when index changes
    useEffect(() => {
        if (isLoading && loadingMessageIndex < loadingMessages.length) {
            setIsTyping(true);
        }
    }, [loadingMessageIndex, isLoading]);

    // Function to load checkpoints for active form
    const loadActiveCheckpoints = useCallback(async () => {
        if (!activeFormName) return;
        
        setIsLoadingCheckpoints(true);
        try {
            const data = await api.chat.getFormCheckpoints(activeFormName);
            setActiveCheckpoints(data);
        } catch (err) {
            console.error('Error loading checkpoints:', err);
            // Don't show error to user, just log it
        } finally {
            setIsLoadingCheckpoints(false);
        }
    }, [activeFormName]);

    // Poll for checkpoint updates every 10 seconds when active form is set
    useEffect(() => {
        if (activeFormName) {
            // Load checkpoints immediately
            loadActiveCheckpoints();
            
            // Set up polling every 3 minutes
            checkpointPollIntervalRef.current = setInterval(() => {
                loadActiveCheckpoints();
            }, 180000); // 3 minutes

            return () => {
                if (checkpointPollIntervalRef.current) {
                    clearInterval(checkpointPollIntervalRef.current);
                }
            };
        } else {
            // Clear checkpoints when no active form
            setActiveCheckpoints(null);
            if (checkpointPollIntervalRef.current) {
                clearInterval(checkpointPollIntervalRef.current);
            }
        }
    }, [activeFormName, loadActiveCheckpoints]);

    // Poll for recently created checkpoints when a conversion request is sent
    useEffect(() => {
        // Start polling for recent checkpoints when loading (conversion in progress)
        if (isLoading) {
            const checkForRecentCheckpoints = async () => {
                try {
                    const data = await api.chat.getRecentCheckpoints(5); // Check last 5 minutes
                    if (data.recent_forms && data.recent_forms.length > 0) {
                        // Get the most recent form
                        const mostRecentForm = data.recent_forms[0];
                        const formName = mostRecentForm.form_name;
                        
                        // ONLY set as active if we don't already have one (fallback)
                        // Don't override the form name extracted from the query
                        if (!activeFormName) {
                            console.log('No active form set, using recent checkpoint for form:', formName);
                            setActiveFormName(formName);
                            // Load checkpoints immediately with the new form name
                            const loadCheckpointsForForm = async () => {
                                setIsLoadingCheckpoints(true);
                                try {
                                    const checkpointData = await api.chat.getFormCheckpoints(formName);
                                    setActiveCheckpoints(checkpointData);
                                } catch (err) {
                                    console.error('Error loading checkpoints:', err);
                                } finally {
                                    setIsLoadingCheckpoints(false);
                                }
                            };
                            loadCheckpointsForForm();
                        } else {
                            // We already have an active form from the query, just update its checkpoints
                            console.log('Active form already set:', activeFormName, '- keeping it');
                        }
                    }
                } catch (err) {
                    console.error('Error checking for recent checkpoints:', err);
                }
            };
            
            // Check immediately, then every 3 minutes while loading
            checkForRecentCheckpoints();
            recentCheckpointsPollIntervalRef.current = setInterval(checkForRecentCheckpoints, 180000); // 3 minutes
            
            return () => {
                if (recentCheckpointsPollIntervalRef.current) {
                    clearInterval(recentCheckpointsPollIntervalRef.current);
                }
            };
        } else {
            // Stop polling when not loading
            if (recentCheckpointsPollIntervalRef.current) {
                clearInterval(recentCheckpointsPollIntervalRef.current);
            }
        }
    }, [isLoading, activeFormName]);

    // Helper function to detect if a query is a conversion request
    const isConversionRequest = (query, responseData) => {
        // Check if query contains conversion-related keywords
        const conversionKeywords = ['convert', 'conversion', 'migrate', 'transform', 'form', 'generate', 'brd', 'document'];
        const lowerQuery = query.toLowerCase();
        const hasConversionKeyword = conversionKeywords.some(keyword => lowerQuery.includes(keyword));
        
        // Check if response indicates a conversion started
        // You may need to adjust this based on your actual API response structure
        if (responseData?.form_name || responseData?.conversion_started) {
            return true;
        }
        
        return hasConversionKeyword;
    };

    // Helper function to extract form name from response or query
    const extractFormName = (query, responseData) => {
        // First try to get from response
        if (responseData?.form_name) {
            return responseData.form_name;
        }
        
        // Try to extract from response content
        const assistantContent = extractLastAssistantMessage(responseData);
        if (assistantContent) {
            // Look for form name patterns in the response - various formats
            const patterns = [
                /form[:\s]+([A-Z0-9_]+)/i,  // "form: DM001" or "form DM001"
                /converting\s+([A-Z0-9_]+)/i,  // "converting DM001"
                /([A-Z0-9_]+)\s+form/i,  // "DM001 form"
            ];
            
            for (const pattern of patterns) {
                const match = assistantContent.match(pattern);
                if (match && match[1]) {
                    return match[1];
                }
            }
        }
        
        // Try to extract from query - look for patterns like "convert DM001" or "generate brd for ebal013"
        const queryPatterns = [
            /generate\s+(?:brd|document|documents?)\s+(?:for|of)\s+([A-Z0-9_]+)/i,  // "generate brd for ebal013"
            /convert\s+([A-Z0-9_]+)/i,  // "convert DM001"
            /convert\s+([A-Z0-9_]+)\s+/i,  // "convert DM001 metadata"
            /form[:\s]+([A-Z0-9_]+)/i,  // "form: DM001" or "form DM001"
            /([A-Z0-9_]+)\s+metadata/i,  // "DM001 metadata"
            /for\s+form\s+([A-Z0-9_]+)/i,  // "for form DM001"
            /\b([A-Z]{2,}[A-Z0-9]{3,})\b/,  // Match any form-like pattern (e.g., EBAL013, DM001)
        ];
        
        for (const pattern of queryPatterns) {
            const match = query.match(pattern);
            if (match && match[1]) {
                return match[1].toUpperCase(); // Normalize to uppercase
            }
        }
        
        return null;
    };

    // Helper function to extract last assistant message from response
    const extractLastAssistantMessage = (responseData) => {
        if (!responseData?.messages) return null;
        
        // Find the last message with role "assistant"
        const assistantMessages = responseData.messages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length === 0) return null;
        
        const lastAssistant = assistantMessages[assistantMessages.length - 1];
        
        // If content is a string, return it directly
        if (typeof lastAssistant.content === 'string') {
            return lastAssistant.content;
        }
        
        // If content is an array, find the text type
        if (Array.isArray(lastAssistant.content)) {
            const textContent = lastAssistant.content.find(item => item.type === 'text');
            if (textContent?.text) {
                return textContent.text;
            }
        }
        
        return null;
    };

    // Helper function to render markdown-like content
    const renderMarkdown = (text) => {
        if (!text) return '';
        
        // Split by lines and process markdown-like syntax
        const lines = text.split('\n');
        return lines.map((line, lineIndex) => {
            const lineKey = `line-${lineIndex}`;
            // Headers
            if (line.startsWith('## ')) {
                return <h2 key={lineKey} className="text-xl font-bold mt-4 mb-2">{line.substring(3)}</h2>;
            }
            if (line.startsWith('### ')) {
                return <h3 key={lineKey} className="text-lg font-semibold mt-3 mb-2">{line.substring(4)}</h3>;
            }
            // Bold text
            if (line.includes('**')) {
                const parts = line.split(/(\*\*.*?\*\*)/g);
                return (
                    <p key={lineKey} className="mb-2">
                        {parts.map((part, partIndex) => {
                            const partKey = `part-${lineIndex}-${partIndex}`;
                            if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={partKey}>{part.slice(2, -2)}</strong>;
                            }
                            return <span key={partKey}>{part}</span>;
                        })}
                    </p>
                );
            }
            // Code blocks
            if (line.startsWith('`') && line.endsWith('`')) {
                return <code key={lineKey} className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{line.slice(1, -1)}</code>;
            }
            // List items
            if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
                return <li key={lineKey} className="ml-4 mb-1">{line.trim().substring(2)}</li>;
            }
            // Numbered list
            if (/^\d+\.\s/.test(line.trim())) {
                return <li key={lineKey} className="ml-4 mb-1 list-decimal">{line.trim().replace(/^\d+\.\s/, '')}</li>;
            }
            // Empty line
            if (line.trim() === '') {
                return <br key={lineKey} />;
            }
            // Regular paragraph
            return <p key={lineKey} className="mb-2">{line}</p>;
        });
    };

    const handleTaskComplete = useCallback((taskId, taskData) => {
        if (!taskId || handledCompletedTaskIdsRef.current.has(taskId)) {
            return;
        }
        handledCompletedTaskIdsRef.current.add(taskId);

        const allMessages = taskData?.messages || taskData?.result?.messages;
        const assistantContent = extractLastAssistantMessage({ messages: allMessages || [] });

        if (assistantContent) {
            setMessages(prev => [...prev, {
                id: Date.now(),
                type: 'ai',
                content: assistantContent,
                timestamp: new Date()
            }]);
        }

        if (taskData?.result?.form_name) {
            setActiveFormName(taskData.result.form_name);
        }
    }, []);

    const handleTaskError = useCallback((taskId, error) => {
        if (!taskId || handledFailedTaskIdsRef.current.has(taskId)) {
            return;
        }
        handledFailedTaskIdsRef.current.add(taskId);

        setMessages(prev => [...prev, {
            id: Date.now(),
            type: 'ai',
            content: `❌ Conversion failed: ${error}`,
            timestamp: new Date()
        }]);
    }, []);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: inputValue.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        // Reset input height and remove scrollbar
        if (inputRef.current) {
            inputRef.current.style.height = '48px';
            inputRef.current.classList.remove('show-scrollbar');
        }
        setIsLoading(true);
        setLoadingMessageIndex(0);
        setIsTyping(true);

        try {
            // Check if this is likely a conversion query
            const isConversion = isConversionRequest(userMessage.content, {});
            
            // Call API with async_mode=true for conversion queries
            const responseData = await api.chat.sendQuery(userMessage.content, isConversion);
            console.log('Backend Response:', responseData);

            // Handle async mode response (task-based)
            if (responseData.async_mode && responseData.task_id) {
                console.log('Async mode enabled, task_id:', responseData.task_id);
                
                // Add a task monitoring message
                const taskMessage = {
                    id: Date.now() + 1,
                    type: 'task',
                    task_id: responseData.task_id,
                    content: responseData.message || 'Processing your conversion request...',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, taskMessage]);
                setIsLoading(false);
                setLoadingMessage('');
                setLoadingMessageIndex(0);
                setIsTyping(false);
                return; // Exit early for async mode
            }

            // Synchronous response handling (existing logic)
            // Check if this is a conversion request and extract form name from query
            // IMPORTANT: Prioritize form name from query over recent checkpoints
            if (isConversionRequest(userMessage.content, responseData)) {
                const formName = extractFormName(userMessage.content, responseData);
                if (formName) {
                    console.log('Setting active form from query:', formName);
                    setActiveFormName(formName);
                } else {
                    console.log('Could not extract form name from query, will use recent checkpoints');
                    // If we can't extract form name, fallback will be handled by recent checkpoints polling
                }
            }

            const assistantBase =
                (typeof responseData.message === 'string' && responseData.message.trim()
                    ? responseData.message.trim()
                    : null) || extractLastAssistantMessage(responseData);
            let assistantContent = assistantBase || '';
            if (responseData.error?.suggestion && responseData.status === 'failed') {
                assistantContent = assistantContent
                    ? `${assistantContent}\n\n—\n${responseData.error.suggestion}`
                    : responseData.error.suggestion;
            }
            if (responseData.error?.rephrase_hint) {
                assistantContent = assistantContent
                    ? `${assistantContent}\n\n💡 ${responseData.error.rephrase_hint}`
                    : `💡 ${responseData.error.rephrase_hint}`;
            }

            const aiMessageId = Date.now() + 1;
            if (assistantContent) {
                const aiMessage = {
                    id: aiMessageId,
                    type: 'ai',
                    content: assistantContent,
                    timestamp: new Date(),
                    responseStatus: responseData.status,
                    retryable: responseData.error?.retryable === true,
                    lastUserQuery: userMessage.content,
                };
                setMessages(prev => [...prev, aiMessage]);
            } else {
                const errorMessage = {
                    id: aiMessageId,
                    type: 'ai',
                    content: 'Sorry, I could not process the response. Please try again.',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, errorMessage]);
            }

            const jobId = responseData.job_id || responseData.validation_info?.job_id;
            const protocolUrl = responseData.protocol_url || responseData.validation_info?.protocol_url;
            if (protocolUrl) {
                window.location.href = protocolUrl;
            }
            if (jobId && assistantContent) {
                const base = (networkManager.QA_AGENT_URL || '').replace(/\/$/, '');
                if (base) {
                    const pollTimer = setInterval(async () => {
                        try {
                            const r = await fetch(
                                `${base}/automation/job-status?job_id=${encodeURIComponent(jobId)}`,
                            );
                            if (!r.ok) return;
                            const data = await r.json();
                            if (typeof data.message === 'string') {
                                setMessages((prev) =>
                                    prev.map((m) =>
                                        m.id === aiMessageId ? { ...m, content: data.message } : m,
                                    ),
                                );
                            }
                            if (['completed', 'failed', 'expired'].includes(data.status)) {
                                clearInterval(pollTimer);
                            }
                        } catch (_) {
                            /* ignore transient poll errors */
                        }
                    }, 3000);
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                id: Date.now() + 1,
                type: 'ai',
                content: `❌ Error: ${error.message || 'An error occurred. Please try again.'}`,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
            setLoadingMessageIndex(0);
            setIsTyping(false);
            // Clear checkpoints when final response is received
            setActiveFormName(null);
            setActiveCheckpoints(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend(e);
        }
    };

    const handleOpenReportModal = async () => {
        setShowReportModal(true);
        setReportType('metadata'); // Default to metadata
        setIsLoadingReports(true);
        setSelectedReport(null);
        setReportContent('');
        
        try {
            // Load available report types
            const typesData = await api.chat.listReportTypes();
            setReportTypes(typesData.report_types || []);
            
            // Load reports for default type
            const reportList = await api.chat.listReports('metadata');
            setReports(reportList.reports || []);
        } catch (error) {
            console.error('Error fetching reports:', error);
            setReports([]);
            setReportTypes([]);
        } finally {
            setIsLoadingReports(false);
        }
    };

    const handleCloseReportModal = () => {
        setShowReportModal(false);
        setSelectedReport(null);
        setReportContent('');
        setReportType('metadata');
    };

    const handleReportTypeChange = async (type) => {
        setReportType(type);
        setSelectedReport(null);
        setReportContent('');
        setIsLoadingReports(true);
        
        try {
            const reportList = await api.chat.listReports(type);
            setReports(reportList.reports || []);
        } catch (error) {
            console.error('Error fetching reports:', error);
            setReports([]);
        } finally {
            setIsLoadingReports(false);
        }
    };

    const handleSelectReport = async (report) => {
        setSelectedReport(report);
        setIsLoadingReportContent(true);
        
        try {
            const content = await api.chat.getReportByTypeAndForm(report.report_type, report.form_name, 'html');
            setReportContent(content);
        } catch (error) {
            console.error('Error fetching report content:', error);
            setReportContent(`<div style="padding: 20px; color: red;">Error loading report: ${error.message}</div>`);
        } finally {
            setIsLoadingReportContent(false);
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-120px)] max-h-none chat-container">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 pb-3 border-b chat-header" style={{borderBottomColor: 'var(--border-color)'}}>
                <div>
                    <h2 className="page-title" style={{color: 'var(--text-primary)'}}>Oracle Forms Modernization</h2>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowArtifactsModal(true)}
                        className="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 shadow-sm hover:shadow-md"
                        style={{
                            backgroundColor: 'var(--blue-primary)',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    >
                        Artifacts
                    </button>
                    <button
                        onClick={() => setShowFormFeedbackModal(true)}
                        className="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 shadow-sm hover:shadow-md"
                        style={{
                            backgroundColor: 'var(--green-primary, #22c55e)',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    >
                        Form Feedback
                    </button>
                    <button
                        onClick={() => setShowCheckpointsModal(true)}
                        className="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 shadow-sm hover:shadow-md"
                        style={{
                            backgroundColor: 'var(--blue-primary)',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    >
                        Conversion Checkpoints
                    </button>
                    <button
                        onClick={() => setShowTestCasesModal(true)}
                        className="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 shadow-sm hover:shadow-md"
                        style={{
                            backgroundColor: 'var(--blue-primary)',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    >
                        Test Cases
                    </button>
                    <button
                        onClick={handleOpenReportModal}
                        className="px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 shadow-sm hover:shadow-md"
                        style={{
                            backgroundColor: 'var(--blue-primary)',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    >
                        Conversion Test Report
                    </button>
                </div>
            </div>

            {/* Messages Container */}
            <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto mb-4 space-y-6 px-4 py-4 min-h-0 chat-messages auto-hide-scrollbar" 
                style={{backgroundColor: 'var(--bg-primary)'}}
            >
                {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center empty-chat-state">
                            <div className="text-5xl mb-4 empty-chat-icon">💬</div>
                            <p className="text-xl font-semibold" style={{color: 'var(--text-primary)'}}>Start a conversation</p>
                            <p className="text-sm mt-2" style={{color: 'var(--text-secondary)'}}>Ask me anything about Oracle Forms Modernization</p>
                        </div>
                    </div>
                ) : (
                    messages.map((message) => (
                        <div
                            key={message.id}
                            className={`flex gap-4 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            {message.type === 'task' ? (
                                // Task-based conversion monitoring
                                <div className="w-full max-w-[75%] md:max-w-[65%]">
                                    <ConversionTaskMonitor
                                        taskId={message.task_id}
                                        apiMethod={api.chat.getQueryTaskStatus}
                                        onComplete={(taskData) => {
                                            console.log('Task completed:', message.task_id, taskData);
                                            handleTaskComplete(message.task_id, taskData);
                                        }}
                                        onError={(error) => {
                                            console.error('Task failed:', message.task_id, error);
                                            handleTaskError(message.task_id, error);
                                        }}
                                    />
                                </div>
                            ) : (
                                <>
                                    {message.type === 'ai' && (
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 8V4H8"/>
                                                <rect width="16" height="12" x="4" y="8" rx="2"/>
                                                <path d="M2 14h2"/>
                                                <path d="M20 14h2"/>
                                                <path d="M15 13v2"/>
                                                <path d="M9 13v2"/>
                                            </svg>
                                        </div>
                                    )}
                                    <div
                                        className={`max-w-[75%] md:max-w-[65%] rounded-2xl px-4 py-3 shadow-sm ${
                                            message.type === 'user'
                                                ? 'message-user'
                                                : 'message-ai'
                                        }`}
                                        style={{
                                            backgroundColor: message.type === 'user' ? 'var(--blue-primary)' : 'var(--bg-secondary)',
                                            color: message.type === 'user' ? 'white' : 'var(--text-primary)',
                                            borderColor: message.type === 'ai' ? 'var(--border-color)' : 'transparent',
                                            borderWidth: message.type === 'ai' ? '1px' : '0',
                                            borderStyle: 'solid'
                                        }}
                                    >
                                        <div className="whitespace-pre-wrap break-words leading-relaxed">
                                            {message.type === 'ai' ? (
                                                <div className="prose prose-sm max-w-none">
                                                    {renderMarkdown(message.content)}
                                                    {message.retryable && message.lastUserQuery && (
                                                        <button
                                                            type="button"
                                                            className="mt-3 text-sm font-medium px-3 py-1.5 rounded-lg border"
                                                            style={{
                                                                borderColor: 'var(--border-color)',
                                                                color: 'var(--blue-primary)',
                                                                background: 'var(--bg-primary)',
                                                            }}
                                                            onClick={() => {
                                                                setInputValue(message.lastUserQuery);
                                                                if (inputRef.current) {
                                                                    inputRef.current.focus();
                                                                }
                                                            }}
                                                        >
                                                            Retry
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                message.content
                                            )}
                                        </div>
                                        <div
                                            className="text-xs mt-2 opacity-70"
                                            style={{
                                                color: message.type === 'user' ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)'
                                            }}
                                        >
                                            {message.timestamp.toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </div>
                                    </div>
                                    {message.type === 'user' && (
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white" style={{backgroundColor: 'var(--blue-primary)'}}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                                <circle cx="12" cy="7" r="4"/>
                                            </svg>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))
                )}
                {isLoading && (
                    <>
                        <div className="flex justify-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 8V4H8"/>
                                    <rect width="16" height="12" x="4" y="8" rx="2"/>
                                    <path d="M2 14h2"/>
                                    <path d="M20 14h2"/>
                                    <path d="M15 13v2"/>
                                    <path d="M9 13v2"/>
                                </svg>
                            </div>
                            <div 
                                className="loading-indicator rounded-2xl px-4 py-3 max-w-[75%] md:max-w-[65%] shadow-sm"
                                style={{
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    borderColor: 'var(--border-color)',
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                            >
                                <div className="flex items-center space-x-2">
                                    <div className="flex space-x-1">
                                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted)', animationDelay: '0ms' }}></div>
                                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted)', animationDelay: '150ms' }}></div>
                                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted)', animationDelay: '300ms' }}></div>
                                    </div>
                                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                        {loadingMessage || loadingMessages[loadingMessageIndex] || 'Thinking...'}
                                        {isTyping && <span className="inline-block w-1 h-4 ml-1 animate-pulse" style={{ backgroundColor: 'var(--text-muted)' }}>|</span>}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* Checkpoints Display - Only shown during loading */}
                        {activeFormName && isLoading && (
                            <div className="flex justify-start gap-4 mt-4">
                                <div className="flex-shrink-0 w-8 h-8"></div>
                                <div 
                                    className="rounded-2xl px-4 py-3 max-w-[75%] md:max-w-[65%] shadow-sm"
                                    style={{
                                        backgroundColor: 'var(--bg-primary)',
                                        borderColor: 'var(--border-color)',
                                        borderWidth: '1px',
                                        borderStyle: 'solid'
                                    }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                            Conversion Progress: {activeCheckpoints?.form_name || activeFormName}
                                        </h4>
                                        {isLoadingCheckpoints && (
                                            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                                Updating...
                                            </div>
                                        )}
                                    </div>
                                    {isLoadingCheckpoints && !activeCheckpoints ? (
                                        <div className="text-xs py-2" style={{ color: 'var(--text-secondary)' }}>
                                            Loading checkpoints...
                                        </div>
                                    ) : activeCheckpoints && activeCheckpoints.checkpoints && Object.keys(activeCheckpoints.checkpoints).length > 0 ? (
                                        <div className="space-y-2">
                                            {Object.values(activeCheckpoints.checkpoints).map((checkpoint) => {
                                                const isCompleted = checkpoint.completed;
                                                return (
                                                    <div
                                                        key={checkpoint.id}
                                                        className={`flex items-center gap-2 text-xs ${!isCompleted ? 'checkpoint-pending' : ''}`}
                                                        style={{
                                                            animation: !isCompleted ? 'checkpointBlink 1.5s ease-in-out infinite' : 'none'
                                                        }}
                                                    >
                                                        <div style={{ color: isCompleted ? '#10b981' : '#6b7280' }}>
                                                            {isCompleted ? (
                                                                <IoCheckmarkCircle className="w-4 h-4" />
                                                            ) : (
                                                                <IoCloseCircle className="w-4 h-4" />
                                                            )}
                                                        </div>
                                                        <span 
                                                            className="flex-1 truncate"
                                                            style={{ 
                                                                color: isCompleted ? 'var(--text-primary)' : 'var(--text-secondary)',
                                                            }}
                                                        >
                                                            {checkpoint.name}
                                                        </span>
                                                        <span 
                                                            className="text-xs font-medium"
                                                            style={{ color: isCompleted ? '#10b981' : '#6b7280' }}
                                                        >
                                                            {isCompleted ? 'Completed' : 'Pending'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-xs py-2" style={{ color: 'var(--text-secondary)' }}>
                                            Checkpoints will appear here once the conversion starts...
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
                {/* Scroll marker removed - using direct container scroll */}
            </div>

            {/* Input Area */}
            <div 
                className="chat-input-area pt-3 pb-1"
                style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderTopColor: 'var(--border-color)',
                    borderTopWidth: '1px',
                    borderTopStyle: 'solid'
                }}
            >
                <form onSubmit={handleSend} className="flex items-start gap-3 max-w-4xl mx-auto px-4">
                    <div className="flex-1 relative">
                        <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                                // Auto-resize textarea
                                e.target.style.height = 'auto';
                                const newHeight = Math.min(e.target.scrollHeight, 200);
                                e.target.style.height = newHeight + 'px';
                                
                                // Show scrollbar only after 3 lines (~72px)
                                if (newHeight > 72) {
                                    e.target.classList.add('show-scrollbar');
                                } else {
                                    e.target.classList.remove('show-scrollbar');
                                }
                            }}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your message here..."
                            rows={1}
                            className="chat-input w-full px-4 py-3 pr-12 rounded-xl focus:outline-none focus:ring-2 resize-none overflow-y-auto"
                            style={{
                                minHeight: '48px',
                                maxHeight: '200px',
                                backgroundColor: 'var(--bg-tertiary)',
                                color: 'var(--text-primary)',
                                borderColor: 'var(--border-color)',
                                borderWidth: '1px',
                                borderStyle: 'solid'
                            }}
                            disabled={isLoading}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={!inputValue.trim() || isLoading}
                        className="chat-send-button flex-shrink-0 h-[48px] px-6 rounded-xl font-medium transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 shadow-sm hover:shadow-md"
                        style={{
                            backgroundColor: !inputValue.trim() || isLoading ? 'var(--bg-tertiary)' : 'var(--blue-primary)',
                            color: !inputValue.trim() || isLoading ? 'var(--text-muted)' : 'white',
                            cursor: !inputValue.trim() || isLoading ? 'not-allowed' : 'pointer',
                            focusRingColor: 'var(--blue-primary)'
                        }}
                    >
                        <IoSend className="w-5 h-5" />
                    </button>
                </form>
                <p className="text-xs mt-2 text-center" style={{ color: 'var(--text-muted)' }}>
                    Press Enter to send, Shift+Enter for new line
                </p>
            </div>

            {/* Report Modal */}
            {showReportModal && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                    onClick={handleCloseReportModal}
                >
                    <div 
                        className="flex flex-col bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh]"
                        style={{ backgroundColor: 'var(--bg-primary)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-4 border-b" style={{ borderBottomColor: 'var(--border-color)' }}>
                            <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                                Conversion Test Reports
                            </h3>
                            <button
                                onClick={handleCloseReportModal}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                style={{ color: 'var(--text-primary)' }}
                            >
                                <IoClose className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Report Type Tabs */}
                        <div className="flex border-b overflow-x-auto" style={{ borderBottomColor: 'var(--border-color)' }}>
                            {(() => {
                                // Helper function to get display name for report type
                                const getReportTypeDisplayName = (type) => {
                                    const displayNames = {
                                        'metadata': 'Metadata',
                                        'html': 'HTML',
                                        'ts': 'TypeScript',
                                        'integrated': 'Integrated UI',
                                        'backend': 'Backend'
                                    };
                                    return displayNames[type] || type.charAt(0).toUpperCase() + type.slice(1);
                                };

                                if (reportTypes.length > 0) {
                                    return reportTypes.map((typeInfo) => {
                                        const type = typeInfo.type;
                                        const isActive = reportType === type;
                                        return (
                                            <button
                                                key={type}
                                                onClick={() => handleReportTypeChange(type)}
                                                className={`flex-shrink-0 px-6 py-3 font-medium transition-all duration-200 ${
                                                    isActive ? 'border-b-2' : ''
                                                }`}
                                                style={{
                                                    color: isActive 
                                                        ? 'var(--blue-primary)' 
                                                        : 'var(--text-secondary)',
                                                    borderBottomColor: isActive 
                                                        ? 'var(--blue-primary)' 
                                                        : 'transparent',
                                                }}
                                                title={typeInfo.description}
                                            >
                                                {getReportTypeDisplayName(type)}
                                            </button>
                                        );
                                    });
                                } else {
                                    return (
                                // Fallback tabs if types not loaded
                                <>
                                    <button
                                        onClick={() => handleReportTypeChange('metadata')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'metadata' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'metadata' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'metadata' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        Metadata
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('html')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'html' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'html' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'html' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        HTML
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('ts')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'ts' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'ts' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'ts' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        TypeScript
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('integrated')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'integrated' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'integrated' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'integrated' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        Integrated UI
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('backend')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'backend' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'backend' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'backend' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        Backend
                                    </button>
                                        </>
                                    );
                                }
                            })()}
                        </div>

                        {/* Modal Content */}
                        <div className="flex flex-1 overflow-hidden">
                            {/* Report List Sidebar */}
                            <div 
                                className="w-64 border-r overflow-y-auto p-4"
                                style={{ 
                                    borderRightColor: 'var(--border-color)',
                                    backgroundColor: 'var(--bg-secondary)'
                                }}
                            >
                                {isLoadingReports ? (
                                    <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                                        Loading reports...
                                    </div>
                                ) : reports.length === 0 ? (
                                    <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                                        No reports found
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {reports.map((report) => {
                                            const isSelected = selectedReport && 
                                                selectedReport.form_name === report.form_name && 
                                                selectedReport.report_type === report.report_type;
                                            return (
                                                <button
                                                    key={`${report.report_type}-${report.form_name}`}
                                                    onClick={() => handleSelectReport(report)}
                                                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                                                        isSelected
                                                            ? 'font-semibold'
                                                            : 'hover:bg-gray-100'
                                                    }`}
                                                    style={{
                                                        backgroundColor: isSelected 
                                                            ? 'var(--blue-primary)' 
                                                            : 'transparent',
                                                        color: isSelected 
                                                            ? 'white' 
                                                            : 'var(--text-primary)',
                                                    }}
                                                    title={report.exists ? `Report exists for ${report.form_name}` : 'Report not found'}
                                                >
                                                    <div className="flex flex-col">
                                                        <span>{report.form_name}</span>
                                                        {report.exists && (
                                                            <span className="text-xs opacity-75 mt-1">
                                                                {report.html_path ? 'HTML' : ''}
                                                                {report.html_path && report.json_path ? ' • ' : ''}
                                                                {report.json_path ? 'JSON' : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Report Content Area */}
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {selectedReport ? (
                                    <>
                                        {isLoadingReportContent ? (
                                            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                                Loading report content...
                                            </div>
                                        ) : selectedReport.exists ? (
                                            <iframe
                                                srcDoc={reportContent}
                                                className="w-full h-full border-0"
                                                title={`${selectedReport.form_name} - ${selectedReport.report_type}`}
                                                style={{ backgroundColor: 'white' }}
                                            />
                                        ) : (
                                            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                                Report not found for {selectedReport.form_name} ({selectedReport.report_type})
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                        Select a report from the list to view
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Artifacts Modal */}
            <Artifacts 
                isOpen={showArtifactsModal}
                onClose={() => setShowArtifactsModal(false)}
            />

            {/* Form Feedback Modal */}
            <FormFeedback 
                isOpen={showFormFeedbackModal}
                onClose={() => setShowFormFeedbackModal(false)}
            />

            {/* Conversion Checkpoints Modal */}
            <ConversionCheckpoints 
                isOpen={showCheckpointsModal}
                onClose={() => setShowCheckpointsModal(false)}
            />

            {/* Test Cases Modal */}
            <TestCases 
                isOpen={showTestCasesModal}
                onClose={() => setShowTestCasesModal(false)}
            />
        </div>
    );
};

export default ChatInterface;

